/* eslint-disable no-param-reassign */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, type FC } from 'react';

import { MaskIcon } from '@/app/common/icons/MaskIcon';
// eslint-disable-next-line import/no-cycle
import { FieldType, type Target } from '../../../index';
import { sumClasses } from '../../../../../utils/misc';

export const selectSVG = <MaskIcon category="Arrows" name="icon-small-chevron-down" size={29} footprint={24} />;

type ButtonProps = {
  buttonId: string;
  labelId: string;
  innerRef: any;
  defaultProps: any;
  disabled?: boolean;
  name: string;
  options: any;
  currentSelection: any;
  currentIndex: number;
  value: any;
  listClassName: string;
  addButtonProps: any;
  defaultValue: any;
  updateOnClickOut?: boolean;
  type?: FieldType;
  expanded?: boolean;
  adjustHeight: () => void;
  onUpdate: (e: Target) => Promise<void>;
  setSelected: (index: any) => void;
  setCurrentSelection: React.Dispatch<any>;
  setCurrentIndex: React.Dispatch<any>;
  setListClassName: React.Dispatch<React.SetStateAction<string>>;
  scrollOnFocus: (item: any, newIndex: any) => void;
  setButtonProps: React.Dispatch<
    React.SetStateAction<
      | {
          'aria-expanded': string;
        }
      | {
          'aria-expanded'?: undefined;
        }
    >
  >;
  handleClickOut: (val: any) => void;
};

const Button: FC<ButtonProps> = (props) => {
  const {
    buttonId,
    labelId,
    innerRef,
    defaultProps,
    disabled,
    adjustHeight,
    name,
    onUpdate,
    options,
    currentSelection,
    currentIndex,
    setSelected,
    setCurrentSelection,
    setCurrentIndex,
    setListClassName,
    scrollOnFocus,
    value,
    listClassName,
    addButtonProps,
    setButtonProps,
    defaultValue,
    updateOnClickOut,
    type,
    handleClickOut,
    expanded,
  } = props;

  useEffect(() => {
    adjustHeight();
  }, [addButtonProps, expanded]);

  const handleButtonClick = (e: any, val: any) => {
    if (listClassName === '') val = 'hidden';
    setListClassName(val);
    setButtonProps(val === '' ? { 'aria-expanded': 'true' } : {});
  };

  const handleButtonKeyDown = (e: KeyboardEvent) => {
    const keyCode = e.key;
    let currentItem = null;
    const handledKeys = ['Enter', 'Escape', ' ', 'End', 'Home', 'ArrowUp', 'ArrowDown'];

    if (handledKeys.indexOf(keyCode) !== -1) {
      const index = type === FieldType.LISTBOX ? 0 : 1;

      switch (keyCode) {
        case 'Enter': {
          e.preventDefault();
          const selected = options[currentIndex];
          if (type === FieldType.LISTBOX) {
            setSelected(currentIndex);
            setCurrentSelection(selected);
            onUpdate({ target: { name, value: selected.value } });
            handleClickOut('hidden');
          } else {
            if (updateOnClickOut && listClassName === '')
              onUpdate({
                target: {
                  name,
                  value: currentSelection.filter((o: any) => o.value !== defaultValue).map((o: any) => o.value),
                },
              });
            setListClassName('hidden');
            setButtonProps({});
          }
          break;
        }
        case 'Escape':
          if (type === FieldType.LISTBOX) {
            setSelected(options.findIndex((o: any) => o.value === currentSelection.value));
            setListClassName(listClassName === '' ? 'hidden' : '');
            setButtonProps(listClassName === '' ? { 'aria-expanded': 'true' } : {});
          } else {
            if (updateOnClickOut && listClassName === '')
              onUpdate({
                target: {
                  name,
                  value: currentSelection.filter((o: any) => o.value !== defaultValue).map((o: any) => o.value),
                },
              });
            setListClassName('hidden');
            setButtonProps({});
          }
          break;
        case 'End':
          scrollOnFocus(options[options.length - 1], options.length - 1);
          setCurrentIndex(options.length - 1);
          break;
        case 'Home':
          scrollOnFocus(options[index], index);
          setCurrentIndex({ currentIndex: index });
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex === index) {
            currentItem = options[options.length - 1];
            scrollOnFocus(currentItem, options.length - 1);
            setCurrentIndex(options.length - 1);
            break;
          }

          currentItem = options[currentIndex - 1];
          scrollOnFocus(currentItem, currentIndex - 1);
          setCurrentIndex(currentIndex - 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex === options.length - 1) {
            currentItem = options[index];
            scrollOnFocus(currentItem, index);
            setCurrentIndex(0);
            break;
          }

          currentItem = options[currentIndex + 1];
          scrollOnFocus(currentItem, currentIndex + 1);
          setCurrentIndex(currentIndex + 1);
          break;
        default:
          break;
      }
    } else {
      const selectedOption =
        type === FieldType.LISTBOX
          ? options.findIndex((o: any) => o.value === currentSelection.value)
          : options.findIndex((o: any) => o.value === currentSelection[0].value);
      setSelected(selectedOption);
      setListClassName('hidden');
      setButtonProps({});
    }
  };

  const selectionLenght = currentSelection && currentSelection.length;
  const isDefaultSelected =
    type === FieldType.LISTBOX
      ? value === defaultProps
      : selectionLenght === 1 && currentSelection.filter((o: any) => o.value === defaultValue).length > 0;
  const displayValue =
    type === FieldType.LISTBOX
      ? null
      : isDefaultSelected
        ? currentSelection.map((o: any) => o.label)
        : currentSelection
            .filter((o: any) => o.value !== defaultValue)
            .map((o: any) => o.label)
            .join(' | ');
  const bottonProps = {
    'aria-haspopup': 'listbox',
    'aria-labelledby': sumClasses([buttonId, labelId]),
    ...addButtonProps,
    ...(disabled ? { 'aria-disabled': 'true' } : {}),
  };

  return (
    <button
      name={buttonId}
      ref={innerRef}
      onClick={(e: any) => handleButtonClick(e, '')}
      onKeyDown={(e: any) => handleButtonKeyDown(e)}
      id={buttonId}
      className={sumClasses([isDefaultSelected ? 'input-default' : ''])}
      disabled={disabled}
      {...bottonProps}
    >
      {type === FieldType.LISTBOX && currentSelection ? currentSelection.label : displayValue}
      {selectSVG}
    </button>
  );
};

export default Button;
